// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Tab } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseTabList, BaseToolbar, UnderDevelopment } from '~/components/common';
import { DocumentsQuoteTab, InfoQuoteTab, OverbookedQuoteTab } from '~/components/module/quotes';
// Others
import { StorageEnum } from '~/utils/enum/base';
import { IBaseTabItem } from '~/utils/interface/common';
import { EMPTY_STRING } from '~/utils/constants/common';
import { QuoteTabEnum, RoleEnum, TypeQuoteDetailEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { QUOTE_PERMISSIONS } from '~/utils/constants/permissions';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailQuote } from '~/thunks/quote/quoteThunk';
import { IQueryParamQuoteDetail, IQuoteDetail } from '~/utils/interface/quotes';
// Styles, images, icons
import styles from './QuotesDetail.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const QuotesDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  const { canEditQuotes } = usePermissions(QUOTE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const { hash } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: quoteId } = useParams();
  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;

  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(QuoteTabEnum.INFORMATION);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);

  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [detailQuote, setDetailQuote] = useState<IQuoteDetail>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [{ label: QuoteTabEnum.INFORMATION, component: <InfoQuoteTab /> }];

    if (Number(detailQuote?.numberOverbooking || 0) > 0) {
      items.push({
        label: QuoteTabEnum.OVERBOOKED_ITEMS,
        component: <OverbookedQuoteTab />,
        number: Number(detailQuote?.numberOverbooking || 0),
      });
    }

    items.push({ label: QuoteTabEnum.DOCUMENTS, component: <DocumentsQuoteTab /> });

    setTabItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(detailQuote)]);

  useEffect(() => {
    const params: IQueryParamQuoteDetail = {
      quoteId: quoteId || EMPTY_STRING,
      type: TypeQuoteDetailEnum.OVERVIEW,
    };

    handleGetDetailQuote(params);
  }, []);

  useEffect(() => {
    if (hash) setTabKey(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    if (!quoteId) return;

    if (!hash) {
      setTabKey(QuoteTabEnum.INFORMATION);
    }

    getMenuItems();
  }, [quoteId, hash, getMenuItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailQuote = (params: IQueryParamQuoteDetail) => {
    dispatch(getDetailQuote(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        if (!data) return;

        setDetailQuote(data as IQuoteDetail);
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`#${newValue}`);
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleOnBack = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.quotes);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.quotes);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.quotes);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.quotes);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.quotes);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.quotes);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.quotes);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.quotes);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.quotes);
        break;
    }
  };

  const handleExportPdf = () => {
    handleShowUnderDevelopment();
  };

  const handleEditQuote = () => {
    if (!quoteId) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateQuote(quoteId));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateQuote(quoteId));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateQuote(quoteId));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateQuote(quoteId));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateQuote(quoteId));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateQuote(quoteId));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateQuote(quoteId));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateQuote(quoteId));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateQuote(quoteId));
        break;
    }
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function

  return (
    <Box id='quotesDetailPage' className={cx('container')}>
      <BaseToolbar title={t('quote_detail_title_quote')} isBack onBackToPreviousPage={handleOnBack}>
        {tabKey === QuoteTabEnum.INFORMATION && canEditQuotes && (
          <Button
            variant='contained'
            onClick={handleEditQuote}
            startIcon={<img src={icons.commonIconPen} alt={t('common_alt_img')} />}
          >
            {t('table_action_edit', { ns: 'common/base' })}
          </Button>
        )}
      </BaseToolbar>

      <Stack height={5} />

      <BaseTabList value={tabKey} onChange={handleTabChange} type='small'>
        {tabItems.map((item, index) => (
          <Tab
            value={item.label}
            key={index}
            className={cx('tabItem', { active: tabKey === item.label })}
            label={
              <Box className={cx('tabLabel')}>
                <Box>{t(`tab_label_${item.label}`)}</Box>

                {item.number !== undefined && <Box className={cx('tabBadge')}>{item.number}</Box>}
              </Box>
            }
          />
        ))}
      </BaseTabList>

      <Box component={'div'} className={cx('body')} key={quoteId}>
        <Box component={'div'} className={cx('tabContent')}>
          {renderTabContent()}
        </Box>
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default QuotesDetail;
